import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subscription, switchMap, take } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { RearLoadingFrictionlessGuard } from 'src/app/rear-loading/services/rear-loading-frictionless.guard';
import { IProcessOrder } from 'src/app/shared/interfaces/IProcessOrder';
import { selectSettings } from 'src/app/stores/settings/settings.selector';
import { loadSpaces, loadSpacesSuccess } from 'src/app/stores/spaces';
import {
  isSizeSelectionAvailable,
  selectAvailableSpacesCount,
} from 'src/app/stores/spaces/spaces.selector';
import { MAX_USE_ADD_COMPARTMENT } from 'src/environments/runtime';

import { DEVICE_TYPES } from '../../interfaces/ISettings';
import {
  ACTION_TYPES,
  DELIVERY_LIST_TYPE,
  FRICTIONLESS_FLOW,
  MOBILE_TYPE,
  ORDER_ID_TYPE,
  REAR_PATHS,
  SCAN_TYPE,
  TOAST_TYPES,
  VALID_REAR_CODE_TYPES,
  VIEW_TYPES,
} from '../../utils/constants';
import { Utils } from '../../utils/utils';
import { ApiErrorHandlerService } from '../api-error-handler/api-error-handler.service';
import { HelmsdeepService } from '../helmsdeep/helmsdeep.service';
import { ValidateCodeResponse } from '../helmsdeep/types';
import { RearLoadingStore } from '../store/rear-loading/rear-loading.store';

@Injectable({ providedIn: 'root' })
export class RearLoading {
  private lockerIsFullMessage = 'common.lockerIsFullMessage';
  private messageCompartmentAllowed = 'common.messageCompartmentAllowed';
  enableOverflowArea = false;
  deviceType = '';
  flow = '';
  validatePresenceSensor = false;
  isLockerFull = false;
  maxAddCompartment = Number(MAX_USE_ADD_COMPARTMENT);
  subscription: Subscription = new Subscription();

  constructor(
    private helmsdeepService: HelmsdeepService,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private utils: Utils,
    private readonly _rearLoadingStore: RearLoadingStore,
    private store: Store,
    private actions$: Actions,
    private translateService: TranslateService,
    private rearLoadingFrictionlessGuard: RearLoadingFrictionlessGuard
  ) {
    this.subscription.add(
      this.store.select(selectSettings).subscribe((settings) => {
        this.enableOverflowArea = settings.device.enableOverflowArea;
        this.deviceType = settings.device.deviceType;
        this.validatePresenceSensor = settings.device.validatePresenceSensor;
      })
    );
  }

  init(processOrder: IProcessOrder): void {
    this.store.dispatch(loadSpaces());
    this.utils.setLoadingValue(true);
    this.flow = processOrder.flow;

    this.subscription.add(
      this.actions$
        .pipe(
          ofType(loadSpacesSuccess),
          take(1),
          switchMap(() => this.store.select(selectAvailableSpacesCount).pipe(take(1)))
        )
        .subscribe({
          next: (availableSpaces) => {
            const availableSpacesPerSchedule =
              availableSpaces > this.maxAddCompartment ? this.maxAddCompartment : availableSpaces;
            const sessionPositions = this._rearLoadingStore.getSessionPositions();
            this._rearLoadingStore.updateSpacesAvailable(availableSpacesPerSchedule);

            if (this.isFrictionlessFlow() && this.isMaxCompartmentsExceeded(sessionPositions)) {
              this.showMaxCompartmentsAllowedMessage();
              this.utils.setLoadingValue(false);
              return;
            }

            if (this.shouldValidateFaultySpaces()) {
              this.subscription.add(
                this.rearLoadingFrictionlessGuard
                  .validateCanStartDelivery(sessionPositions)
                  .subscribe((canStartDelivery) => {
                    if (!canStartDelivery) {
                      this.utils.setLoadingValue(false);
                      return;
                    }

                    this.continueLoadProcess(availableSpaces, processOrder);
                  })
              );
              return;
            }

            this.continueLoadProcess(availableSpaces, processOrder);
          },
          error: (error) => this.handleError(error),
        })
    );
  }

  private continueLoadProcess(availableSpaces: number, processOrder: IProcessOrder): void {
    if (availableSpaces > 0 || this.isFrictionlessFlow()) {
      this.isLockerFull = false;
      this.processOrderByScreenType(processOrder);
    } else {
      this.isLockerFull = true;
      this.apiErrorHandlerService.showMessage(this.lockerIsFullMessage, TOAST_TYPES.Error);
      this.overflowAreaManagement(processOrder);
    }
  }

  processOrderByScreenType(processOrder: IProcessOrder): void {
    switch (processOrder.type) {
      case SCAN_TYPE:
      case DELIVERY_LIST_TYPE:
        this.validateLoadCode(processOrder);
        break;
      case ORDER_ID_TYPE:
        this.processByOrderId(processOrder);
        break;
      case MOBILE_TYPE:
        this.processByMobile(processOrder);
        break;
    }
  }

  overflowAreaManagement(processOrder: IProcessOrder): void {
    if (this.enableOverflowArea) {
      processOrder.enableOverflowArea = this.enableOverflowArea;
      this.processOrderByScreenType(processOrder);
    } else {
      this.utils.setLoadingValue(false);
    }
  }

  validateLoadCode(processOrder: IProcessOrder): void {
    this.subscription.add(
      this.helmsdeepService.validateCode(`${processOrder.code}`, VALID_REAR_CODE_TYPES).subscribe({
        next: (scheduleInfo) => this.handleScheduleInfo(scheduleInfo, processOrder),
        error: (error) => this.handleError(error),
      })
    );
  }

  private handleScheduleInfo(
    scheduleInfo: ValidateCodeResponse,
    processOrder: IProcessOrder
  ): void {
    processOrder.scheduleId = scheduleInfo.scheduleId;
    processOrder.action = ACTION_TYPES.DELIVERY;

    this.initLoadWithOptionalSizeSelection(processOrder);
  }

  private isFrictionlessFlow(): boolean {
    return this.flow === FRICTIONLESS_FLOW;
  }

  private shouldValidateFaultySpaces(): boolean {
    return this.isFrictionlessFlow() || this.validatePresenceSensor;
  }

  private isMaxCompartmentsExceeded(positions: string[] = []): boolean {
    return positions.length > this.maxAddCompartment;
  }

  private showMaxCompartmentsAllowedMessage(): void {
    this.apiErrorHandlerService.showMessage(
      this.translateService.instant(this.messageCompartmentAllowed, {
        maxCompartments: this.maxAddCompartment,
      }),
      TOAST_TYPES.Info
    );
  }

  private processByOrderId(processOrder: IProcessOrder): void {
    this.subscription.add(
      this.utils.createOrderByOrderIdType(processOrder).subscribe({
        next: (response) => {
          processOrder.scheduleId = response.id;

          this.initLoadWithOptionalSizeSelection(processOrder);
        },
        error: (error) => this.handleError(error),
      })
    );
  }

  private processByMobile(processOrder: IProcessOrder): void {
    this.subscription.add(
      this.utils.createOrderByMobileType(processOrder).subscribe({
        next: (response) => {
          localStorage.setItem('schedule', JSON.stringify(response));
          processOrder.scheduleId = response.id;

          this.initLoadWithOptionalSizeSelection(processOrder);
        },
        error: (error) => this.handleError(error),
      })
    );
  }

  private initLoadWithOptionalSizeSelection(processOrder: IProcessOrder): void {
    processOrder.action = ACTION_TYPES.DELIVERY;
    processOrder.targetScreen = REAR_PATHS.LOADING_ORDER;

    if (this.isArrayDevice()) {
      this.store
        .select(isSizeSelectionAvailable)
        .pipe(take(1))
        .subscribe((sizeSelectionAvailable) => {
          if (sizeSelectionAvailable && !this.isFrictionlessFlow() && !this.isLockerFull) {
            this.goToSizeSelector();
            return;
          }
          this.utils.handleLoadProcess(processOrder);
        });
      return;
    }

    this.utils.handleLoadProcess(processOrder);
  }

  private isArrayDevice(): boolean {
    return this.deviceType === DEVICE_TYPES.ARRAY_RL;
  }

  private goToSizeSelector(): void {
    this.utils.setLoadingValue(false);
    this.utils.redirect(`${VIEW_TYPES.REAR_LOADING}/size-selector`);
  }

  private handleError(error: any): void {
    this.utils.setLoadingValue(false);
    this.apiErrorHandlerService.getErrorMessage(error);
  }
}
